'use client';

import { FC, useRef } from 'react';
import { Button } from '../components/atoms/button';
import clsx from 'clsx';
import { getFontVariable, getRoundedClassName } from '../components/utils';
import {
  PageCollectionConfigFontFamily,
  PageCollectionConfigLanguage,
  PageCollectionConfigRadiusClassName,
  PageFieldsetFieldTypeEnum,
} from '@creator-suite/page-types';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckLotteryStatusSchema,
  CheckLotteryStatusFormType,
} from '@/data/lotteries-dto';
import Link from 'next/link';
import { HaloContainer } from './HaloContainer';
import { getLabelForField } from '@/form-utils';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';

type LotteryCheckResult = {
  status: 'not_found' | 'lottery_not_found' | 'pending' | 'winner' | 'loser';
  prize?: {
    displayName: string;
    description: string;
  };
  callToAction?: {
    label: string;
    source: string;
  };
};

interface CheckLotteryStatusFormProps {
  language: PageCollectionConfigLanguage;
  handleSubmit: (values: CheckLotteryStatusFormType) => Promise<void>;
  buttonRadiusClassName: PageCollectionConfigRadiusClassName;
  inputRadiusClassName: PageCollectionConfigRadiusClassName;
  buttonText: string;
  fontFamily: PageCollectionConfigFontFamily;
  error?: string | null | undefined;
  state: 'initial' | 'loading' | 'success' | 'error';
  result?: LotteryCheckResult;
  backgroundColor: string | null;
  backgroundUrl: string | null;
  countryCode?: string;
}

export const CheckLotteryEntryStatusForm: FC<CheckLotteryStatusFormProps> = (
  props,
) => {
  const {
    language = PageCollectionConfigLanguage.DE,
    handleSubmit,
    buttonRadiusClassName,
    inputRadiusClassName,
    buttonText,
    fontFamily,
    error,
    state,
    result,
    countryCode,
  } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const winnerButtonRef = useRef<HTMLButtonElement>(null);

  const form = useForm<CheckLotteryStatusFormType>({
    resolver: zodResolver(CheckLotteryStatusSchema),
  });

  const {
    formState: { errors, isSubmitting },
    getValues,
  } = form;

  const onSubmit = async (data: CheckLotteryStatusFormType) => {
    await handleSubmit(data);
  };

  // TODO: translate
  const getHeaderText = () => {
    if (state === 'success' && result) {
      switch (result.status) {
        case 'winner':
          return language === 'de'
            ? `Glückwunsch! Du hast gewonnen!\nWir werden uns in Kürze unter der Nummer ${getValues().phone} bei dir melden.`
            : `Congratulations! You won!\nWe will contact you shortly at ${getValues().phone}.`;
        case 'loser':
          return language === 'de'
            ? 'Leider hast du dieses Mal nicht gewonnen.'
            : 'Sorry, you did not win this time.';
        case 'not_found':
          return language === 'de'
            ? 'Diese Telefonnummer hat nicht am Gewinnspiel teilgenommen.'
            : 'This phone number did not participate in the lottery.';
        case 'lottery_not_found':
          return language === 'de'
            ? 'Gewinnspiel wurde nicht gefunden.'
            : 'Lottery was not found.';
        case 'pending':
          return language === 'de'
            ? 'Die Gewinner wurden noch nicht gezogen.'
            : 'Winners have not been drawn yet.';
      }
    }

    return language === 'de'
      ? 'Die Gewinner stehen fest. Nutze das Formular um zu schauen ob du gewonnen hast.'
      : 'The winners have been drawn. Use the form to check if you won.';
  };

  return (
    <div className="flex flex-col space-y-8">
      <div className="space-y-4">
        <p
          className={clsx(
            'mx-auto text-center tracking-tight whitespace-pre-line',
            getFontVariable(fontFamily),
          )}
        >
          {getHeaderText()}
        </p>
        {state === 'success' && result?.status === 'winner' && (
          <div className="space-y-4">
            <div
              className={clsx(
                props.backgroundUrl ? 'bg-black' : '',
                'mx-8 max-w-md rounded-lg border border-gray-200 p-4 text-center',
                getRoundedClassName(buttonRadiusClassName),
              )}
            >
              <p
                className={clsx(
                  'mb-2 font-medium opacity-80',
                  getFontVariable(fontFamily),
                )}
              >
                {language === 'de' ? 'Dein Gewinn:' : 'Your prize:'}
              </p>
              <p
                className={clsx(
                  'mb-2 text-lg font-semibold',
                  getFontVariable(fontFamily),
                )}
              >
                1× {result.prize?.displayName}
              </p>
              {result.prize?.description && (
                <p className={clsx('text-sm', getFontVariable(fontFamily))}>
                  {result.prize.description}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {state === 'success' && result ? (
        result.callToAction && (
          <div className="flex grow items-end justify-center space-y-8">
            <HaloContainer
              elementRef={winnerButtonRef as React.RefObject<HTMLElement>}
            >
              <Link href={result.callToAction.source}>
                <Button
                  ref={winnerButtonRef}
                  className={clsx(
                    props.backgroundUrl ? 'bg-black!' : '',
                    'mx-auto',
                    getRoundedClassName(buttonRadiusClassName),
                    getFontVariable(fontFamily),
                  )}
                  style={
                    props.backgroundColor
                      ? {
                          backgroundColor: props.backgroundColor,
                        }
                      : undefined
                  }
                >
                  <span className="leading-none text-current">
                    {result.callToAction.label}
                  </span>
                </Button>
              </Link>
            </HaloContainer>
          </div>
        )
      ) : (
        <form
          className="flex grow flex-col space-y-8"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex grow flex-col space-y-4">
            <Controller
              name="phone"
              control={form.control}
              render={({ field }) => (
                <PhoneNumberInput
                  placeholder="+49 176 1234 1234"
                  label={getLabelForField(
                    PageFieldsetFieldTypeEnum.MOBILE_PHONE,
                    language,
                  )}
                  error={errors.phone?.message}
                  className={getFontVariable(fontFamily)}
                  roundedClassName={inputRadiusClassName}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  countryCode={countryCode}
                />
              )}
            />
          </div>

          {error && (
            <div className="w-full text-center text-sm text-red-600">
              {error}
            </div>
          )}
          <HaloContainer
            elementRef={buttonRef as React.RefObject<HTMLElement>}
            disableAnimation={isSubmitting || state === 'loading'}
          >
            <Button
              ref={buttonRef}
              type="submit"
              className={clsx(
                props.backgroundUrl ? 'bg-black!' : '',
                'animate-pulse',
                'mx-auto',
                getRoundedClassName(buttonRadiusClassName),
                getFontVariable(fontFamily),
              )}
              style={
                props.backgroundColor
                  ? {
                      backgroundColor: props.backgroundColor,
                    }
                  : undefined
              }
              disabled={isSubmitting || state === 'loading'}
            >
              <span className="leading-none text-current">{buttonText}</span>
            </Button>
          </HaloContainer>
        </form>
      )}
    </div>
  );
};
